import React from "react";
import { Outlet, useLocation, useNavigate, Link, useParams } from "react-router-dom";
import { Home, PieChart, Users, User, Bell, Plus, Wallet as WalletIcon, Settings as SettingsIcon, LogOut, Receipt, Users2, Shield, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useGroup } from "@/context/GroupContext";
import { usePlatform } from "@/context/PlatformContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import Footer from "@/components/Footer";

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { user, logout } = useAuth();
  const { group, isAdmin, myGroups, loading, error } = useGroup();
  const { settings } = usePlatform();

  const slug = params.slug || group?.slug || "";
  const base = slug ? `/${slug}` : "";

  const mobileNav = [
    { to: base || "/", icon: Home, label: "Home", testid: "nav-home", exact: true },
    { to: `${base}/reports`, icon: PieChart, label: "Reports", testid: "nav-reports" },
    { to: `${base}/members`, icon: Users, label: "People", testid: "nav-people" },
    { to: "/profile", icon: User, label: "Profile", testid: "nav-profile" },
  ];

  const desktopNav = [
    { to: base || "/", icon: Home, label: "Dashboard", testid: "sidenav-home", exact: true },
    { to: `${base}/reports`, icon: PieChart, label: "Reports", testid: "sidenav-reports" },
    { to: `${base}/members`, icon: Users, label: "People", testid: "sidenav-people" },
    { to: `${base}/settlements`, icon: Receipt, label: "Settlements", testid: "sidenav-settlements" },
    { to: `${base}/notifications`, icon: Bell, label: "Notifications", testid: "sidenav-notifications" },
    { to: `${base}/settings`, icon: SettingsIcon, label: "Settings", testid: "sidenav-settings" },
    { divider: true },
    { to: "/wallet", icon: WalletIcon, label: "My Wallet", testid: "sidenav-wallet" },
    { to: "/profile", icon: User, label: "Profile", testid: "sidenav-profile" },
  ];

  const isActive = (p, exact) => {
    if (exact) return location.pathname === p;
    return location.pathname === p || location.pathname.startsWith(p + "/");
  };

  // If error loading group, show a nice message
  if (slug && error === "not_found") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center bg-white rounded-3xl p-8 card-soft border border-slate-200/60">
          <div className="w-14 h-14 rounded-2xl bg-rose-50 grid place-items-center mx-auto">
            <Users2 className="w-6 h-6 text-rose-500" />
          </div>
          <h1 className="heading text-2xl font-bold text-slate-900 mt-4">Group not found</h1>
          <p className="text-slate-500 text-sm mt-2">
            <span className="font-mono">/{slug}</span> doesn't exist.
          </p>
          <button onClick={() => navigate("/switch")} className="mt-6 px-5 h-11 rounded-full bg-slate-900 text-white text-sm font-semibold">
            Back to your groups
          </button>
        </div>
      </div>
    );
  }
  if (slug && error === "forbidden") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center bg-white rounded-3xl p-8 card-soft border border-slate-200/60">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 grid place-items-center mx-auto">
            <Shield className="w-6 h-6 text-amber-600" />
          </div>
          <h1 className="heading text-2xl font-bold text-slate-900 mt-4">Access denied</h1>
          <p className="text-slate-500 text-sm mt-2">
            You are not a member of this group. Ask the admin to add you.
          </p>
          <button onClick={() => navigate("/switch")} className="mt-6 px-5 h-11 rounded-full bg-slate-900 text-white text-sm font-semibold">
            Back to your groups
          </button>
        </div>
      </div>
    );
  }

  const GroupSwitcher = ({ className = "", testid = "group-switcher" }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={className} data-testid={testid}>
          <div className="w-10 h-10 rounded-2xl bg-slate-900 text-white grid place-items-center heading font-semibold">
            {(group?.name || slug || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
              {isAdmin ? "Admin • Group" : "Group"}
            </div>
            <div className="heading text-sm font-semibold text-slate-900 truncate">
              {group?.name || (loading ? "Loading…" : "Choose group")}
            </div>
          </div>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[240px] rounded-2xl">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Your groups</DropdownMenuLabel>
        {myGroups.map((g) => (
          <DropdownMenuItem
            key={g.id}
            onClick={() => navigate(`/${g.slug}`)}
            className="rounded-xl"
            data-testid={`switcher-group-${g.slug}`}
          >
            <div className="flex items-center gap-2 w-full">
              <div className="w-6 h-6 rounded-lg bg-slate-900 text-white text-[10px] grid place-items-center font-bold">
                {g.name[0]}
              </div>
              <div className="flex-1 truncate">{g.name}</div>
              {g.is_admin && (
                <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                  admin
                </span>
              )}
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/new")} className="rounded-xl" data-testid="switcher-create-group">
          <Plus className="w-4 h-4 mr-2" /> Create new group
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="app-shell">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col p-6 gap-5 border-r border-slate-200/80 sticky top-0 h-screen"
        data-testid="desktop-sidebar"
      >
        <Link to="/" className="flex items-center gap-2.5 px-2 py-1" data-testid="sidebar-logo">
          <div className="w-9 h-9 rounded-2xl bg-slate-900 grid place-items-center">
            <WalletIcon className="w-4 h-4 text-emerald-400" />
          </div>
          <span className="heading text-lg font-bold text-slate-900 tracking-tight">{settings?.title || "GEMS"}</span>
        </Link>

        <GroupSwitcher
          className="text-left p-3 pr-4 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors flex items-center gap-3 w-full"
          testid="sidebar-group-switcher"
        />

        <nav className="flex-1 space-y-1">
          {desktopNav.map((item, idx) => {
            if (item.divider) return <div key={idx} className="my-2 border-t border-slate-200/80" />;
            if (item.adminOnly && !isAdmin) return null;
            const active = isActive(item.to, item.exact);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                  active ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={active ? 2.5 : 2} />
                <span className="text-sm font-semibold">{item.label}</span>
                {item.adminOnly && (
                  <span className="ml-auto text-[9px] uppercase tracking-widest font-bold text-emerald-600">admin</span>
                )}
              </Link>
            );
          })}
        </nav>

        {slug && (
          <button
            onClick={() => navigate(`${base}/add-expense`)}
            className="w-full h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center justify-center gap-2 active:scale-95"
            data-testid="sidebar-add-expense"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
            Add expense
          </button>
        )}

        <div className="flex items-center gap-3 p-3 rounded-2xl border border-slate-200 bg-white">
          <div className="w-9 h-9 rounded-full overflow-hidden ring-1 ring-slate-200">
            {user?.photo ? (
              <img src={user.photo} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-slate-900 text-white grid place-items-center text-xs font-semibold">
                {user?.name?.[0] || "U"}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            className="w-8 h-8 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500 hover:text-rose-600"
            data-testid="sidebar-logout"
            aria-label="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="app-main pb-bottomnav lg:pb-0">
        {/* Mobile header */}
        <header
          className="lg:hidden sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b border-slate-200/70"
          data-testid="app-header"
        >
          <div className="flex items-center justify-between px-5 py-3">
            <GroupSwitcher
              className="flex items-center gap-2 text-left flex-1 min-w-0 max-w-[220px]"
              testid="header-group-switcher"
            />
            <div className="flex items-center gap-2">
              {slug && (
                <Link
                  to={`${base}/notifications`}
                  className="w-10 h-10 rounded-full grid place-items-center bg-slate-100 hover:bg-slate-200 active:scale-95"
                  data-testid="header-notifications-link"
                >
                  <Bell className="w-4 h-4 text-slate-700" />
                </Link>
              )}
              <Link
                to="/profile"
                data-testid="header-profile-link"
                className="w-10 h-10 rounded-full overflow-hidden ring-1 ring-slate-200 active:scale-95"
              >
                {user?.photo ? (
                  <img src={user.photo} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-slate-900 text-white grid place-items-center text-xs font-semibold">
                    {user?.name?.[0] || "U"}
                  </div>
                )}
              </Link>
            </div>
          </div>
        </header>

        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="px-5 lg:px-0 py-4 lg:py-0"
        >
          <Outlet />
        </motion.main>

        {/* FAB - mobile only */}
        {slug && (
          <button
            onClick={() => navigate(`${base}/add-expense`)}
            data-testid="fab-add-expense"
            className="lg:hidden fixed bottom-24 right-6 z-50 h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white grid place-items-center shadow-xl shadow-emerald-600/30 active:scale-95"
            aria-label="Add expense"
          >
            <Plus className="w-6 h-6" strokeWidth={2.5} />
          </button>
        )}

        {/* Bottom nav - mobile only */}
        {slug && (
          <nav
            className="lg:hidden fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-white/85 border-t border-slate-200/80"
            data-testid="bottom-nav"
          >
            <div className="grid grid-cols-4 gap-1 px-3 py-2">
              {mobileNav.map((item) => {
                const active = isActive(item.to, item.exact);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={item.testid}
                    className="flex flex-col items-center gap-1 py-2 rounded-2xl active:scale-95"
                  >
                    <Icon
                      className={`w-5 h-5 ${active ? "text-emerald-600" : "text-slate-400"}`}
                      strokeWidth={active ? 2.5 : 2}
                    />
                    <span className={`text-[11px] font-semibold ${active ? "text-slate-900" : "text-slate-400"}`}>
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </div>
    </div>
  );
}
